import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Calendar, MapPin, Clock, Users, Mail, Phone, Loader2, Send,
  User, ChevronDown, ChevronUp, UserCheck, BadgeCheck, Clock3,
  Plus, Search, X, UserPlus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyOrganizedEventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const navigate = useNavigate();
  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => { fetchMyEvents(); }, []);

  const fetchMyEvents = async () => {
    try {
      const token = getToken();
      const { data } = await axios.get(`${API_URL}/api/events/my-organized`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(data.events || []);
    } catch { toast.error('Fehler beim Laden der Events'); }
    finally { setLoading(false); }
  };

  const today = new Date().toISOString().split('T')[0];
  const upcoming = events.filter(e => e.date >= today);
  const past = events.filter(e => e.date < today);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-['Oswald'] text-3xl font-bold uppercase tracking-wide"
            data-testid="my-events-heading">
            Meine Events
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Events bei denen du als Organisator (Kontaktperson) eingetragen bist
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : events.length === 0 ? (
          <div className="bg-card border border-border p-8 text-center">
            <Calendar className="mx-auto mb-3 text-muted-foreground" size={40} />
            <p className="text-muted-foreground">Du bist aktuell für keine Events als Organisator (Kontaktperson) eingetragen.</p>
          </div>
        ) : (
          <>
            {upcoming.length > 0 && (
              <EventSection title="Kommende Events" count={upcoming.length} events={upcoming}
                expandedId={expandedId} setExpandedId={setExpandedId} navigate={navigate} getToken={getToken} />
            )}
            {past.length > 0 && (
              <EventSection title="Vergangene Events" count={past.length} events={past}
                expandedId={expandedId} setExpandedId={setExpandedId} navigate={navigate} getToken={getToken} />
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function EventSection({ title, count, events, expandedId, setExpandedId, navigate, getToken }) {
  return (
    <div>
      <h2 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">
        {title} ({count})
      </h2>
      <div className="bg-card border border-border divide-y divide-border">
        {events.map(event => (
          <EventRow key={event.id} event={event} isExpanded={expandedId === event.id}
            onToggle={() => setExpandedId(expandedId === event.id ? null : event.id)}
            navigate={navigate} getToken={getToken} />
        ))}
      </div>
    </div>
  );
}

function EventRow({ event, isExpanded, onToggle, navigate, getToken }) {
  const d = new Date(event.date);
  const stats = event.stats || {};
  const regs = stats.registrations_count || 0;
  const totalPers = stats.total_participants || 0;
  const maxP = event.max_participants;

  return (
    <div data-testid={`organized-event-${event.id}`}>
      {/* Row */}
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors" onClick={onToggle}>
        <div className="text-center flex-shrink-0 w-10">
          <div className="text-lg font-bold leading-none">{d.getDate()}</div>
          <div className="text-[10px] uppercase text-muted-foreground font-semibold">
            {d.toLocaleDateString('de-CH', { month: 'short' })}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{event.title}</p>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
            {event.time && <span className="flex items-center gap-1"><Clock size={11} />{event.time} Uhr</span>}
            {event.location && <span className="flex items-center gap-1 truncate"><MapPin size={11} />{event.location}</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right text-xs">
            <div className="font-semibold">
              {regs} Anm.
              {regs !== totalPers && <span className="text-muted-foreground font-normal"> ({totalPers} Pers.)</span>}
            </div>
            {maxP && <div className="text-muted-foreground">max. {maxP}</div>}
          </div>
          {isExpanded ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
        </div>
      </div>

      {/* Expanded */}
      {isExpanded && <ExpandedContent event={event} getToken={getToken} navigate={navigate} />}
    </div>
  );
}

function ExpandedContent({ event, getToken, navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMsgForm, setShowMsgForm] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchRegistrations = async () => {
    try {
      const token = getToken();
      const { data } = await axios.get(`${API_URL}/api/events/${event.id}/registrations/organizer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(data);
    } catch { toast.error('Fehler beim Laden der Teilnehmer'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRegistrations(); }, [event.id]);

  if (loading) return <div className="flex justify-center py-4 border-t border-border"><Loader2 className="animate-spin text-muted-foreground" size={18} /></div>;

  const confirmed = data?.confirmed || [];
  const waitlist = data?.waitlist || [];

  return (
    <div className="border-t border-border bg-muted/10">
      {/* Actions */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border flex-wrap">
        <Button size="sm" onClick={() => { setShowAddForm(!showAddForm); setShowMsgForm(false); }} data-testid="add-participant-btn">
          <UserPlus size={14} className="mr-1.5" />
          {showAddForm ? 'Abbrechen' : 'Teilnehmer hinzufügen'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setShowMsgForm(!showMsgForm); setShowAddForm(false); }} data-testid="organizer-message-btn">
          <Mail size={14} className="mr-1.5" />
          {showMsgForm ? 'Abbrechen' : 'Teilnehmer anschreiben'}
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate(`/events/${event.id}`)} data-testid="view-event-btn">
          Zum Event
        </Button>
      </div>

      {/* Add Participant Form */}
      {showAddForm && (
        <AddParticipantForm event={event} getToken={getToken} onAdded={() => { setShowAddForm(false); fetchRegistrations(); }} />
      )}

      {/* Message Form */}
      {showMsgForm && (
        <MessageForm event={event} getToken={getToken} hasWaitlist={waitlist.length > 0} onSent={() => setShowMsgForm(false)} />
      )}

      {/* Confirmed */}
      <div className="px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <BadgeCheck size={14} className="text-green-600" />
          <span className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
            Angemeldet ({confirmed.length})
          </span>
        </div>
        {confirmed.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">Noch keine Anmeldungen.</p>
        ) : (
          <div className="divide-y divide-border">
            {confirmed.map(reg => <ParticipantRow key={reg.id} reg={reg} />)}
          </div>
        )}
      </div>

      {/* Waitlist */}
      {waitlist.length > 0 && (
        <div className="px-4 py-3 border-t border-border">
          <div className="flex items-center gap-2 mb-2">
            <Clock3 size={14} className="text-yellow-600" />
            <span className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
              Warteliste ({waitlist.length})
            </span>
          </div>
          <div className="divide-y divide-border">
            {waitlist.map(reg => <ParticipantRow key={reg.id} reg={reg} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function ParticipantRow({ reg }) {
  return (
    <div className="flex items-center gap-3 py-2 text-sm" data-testid={`participant-${reg.id}`}>
      <User size={14} className="text-muted-foreground flex-shrink-0" />
      <span className="flex-1 min-w-0 truncate">
        {reg.member_name || 'Unbekannt'}
        {reg.is_guest && <span className="ml-1.5 text-[10px] font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded uppercase">Gast</span>}
        {reg.guests > 0 && <span className="text-muted-foreground ml-1">(+{reg.guests})</span>}
      </span>
      {reg.member_email && (
        <a href={`mailto:${reg.member_email}`} className="text-muted-foreground hover:text-primary flex-shrink-0" title={reg.member_email}>
          <Mail size={14} />
        </a>
      )}
      {reg.phone && (
        <a href={`tel:${reg.phone}`} className="text-muted-foreground hover:text-primary flex-shrink-0" title={reg.phone}>
          <Phone size={14} />
        </a>
      )}
      {reg.checked_in && <UserCheck size={14} className="text-green-600 flex-shrink-0" title="Eingecheckt" />}
    </div>
  );
}

function AddParticipantForm({ event, getToken, onAdded }) {
  const [mode, setMode] = useState('member'); // 'member' or 'guest'
  const [search, setSearch] = useState('');
  const [members, setMembers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guests, setGuests] = useState(0);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);

  useEffect(() => {
    if (mode === 'member' && members.length === 0) {
      setLoadingMembers(true);
      axios.get(`${API_URL}/api/members`, { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(res => setMembers(res.data.members || []))
        .catch(() => {})
        .finally(() => setLoadingMembers(false));
    }
  }, [mode]);

  useEffect(() => {
    if (!search.trim()) { setFiltered([]); return; }
    const q = search.toLowerCase();
    setFiltered(members.filter(m =>
      (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)
    ).slice(0, 8));
  }, [search, members]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = getToken();
      if (mode === 'member') {
        if (!selectedMember) { toast.error('Bitte Mitglied auswählen'); setSaving(false); return; }
        await axios.post(`${API_URL}/api/events/${event.id}/registrations`,
          { member_id: selectedMember.id, guests, notes: notes || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`${selectedMember.name} wurde angemeldet`);
      } else {
        if (!guestName.trim()) { toast.error('Bitte Name eingeben'); setSaving(false); return; }
        await axios.post(`${API_URL}/api/events/${event.id}/registrations/guest`,
          { name: guestName, email: guestEmail || null, phone: guestPhone || null, guests, notes: notes || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`${guestName} wurde als Gast angemeldet`);
      }
      onAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Anmelden');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="px-4 py-3 border-b border-border space-y-3" data-testid="add-participant-form">
      {/* Mode Toggle */}
      <div className="flex gap-1 bg-muted p-0.5">
        <button type="button" onClick={() => { setMode('member'); setSelectedMember(null); }}
          className={`flex-1 text-xs py-1.5 font-medium transition-colors ${mode === 'member' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="mode-member-btn">
          Mitglied
        </button>
        <button type="button" onClick={() => { setMode('guest'); setSearch(''); }}
          className={`flex-1 text-xs py-1.5 font-medium transition-colors ${mode === 'guest' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="mode-guest-btn">
          Gast
        </button>
      </div>

      {mode === 'member' ? (
        <div>
          <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Mitglied suchen</label>
          {selectedMember ? (
            <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 p-2">
              <User size={14} className="text-primary" />
              <span className="text-sm font-medium flex-1">{selectedMember.name}</span>
              <span className="text-xs text-muted-foreground">{selectedMember.email}</span>
              <button type="button" onClick={() => { setSelectedMember(null); setSearch(''); }} className="text-muted-foreground hover:text-foreground">
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name oder E-Mail..."
                className="rounded-none pl-8" autoFocus data-testid="member-search-input" />
              {loadingMembers && <Loader2 size={14} className="absolute right-2.5 top-2.5 animate-spin text-muted-foreground" />}
              {filtered.length > 0 && (
                <div className="absolute z-20 w-full bg-card border border-border shadow-lg max-h-48 overflow-y-auto">
                  {filtered.map(m => (
                    <button key={m.id} type="button" onClick={() => { setSelectedMember(m); setSearch(''); setFiltered([]); }}
                      className="w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors flex items-center gap-2 text-sm"
                      data-testid={`member-option-${m.id}`}>
                      <User size={12} className="text-muted-foreground flex-shrink-0" />
                      <span className="font-medium truncate">{m.name}</span>
                      <span className="text-xs text-muted-foreground ml-auto flex-shrink-0">{m.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Name *</label>
            <Input value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Vor- und Nachname"
              className="rounded-none" required data-testid="guest-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">E-Mail</label>
              <Input value={guestEmail} onChange={e => setGuestEmail(e.target.value)} placeholder="Optional"
                className="rounded-none" type="email" data-testid="guest-email-input" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Telefon</label>
              <Input value={guestPhone} onChange={e => setGuestPhone(e.target.value)} placeholder="Optional"
                className="rounded-none" data-testid="guest-phone-input" />
            </div>
          </div>
        </div>
      )}

      {/* Guests + Notes */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Begleitung</label>
          <Input value={guests} onChange={e => setGuests(Math.max(0, parseInt(e.target.value) || 0))} type="number"
            min="0" max="10" className="rounded-none" data-testid="guests-count-input" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Bemerkungen</label>
          <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional"
            className="rounded-none" data-testid="notes-input" />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Zahlung: Barzahlung</span>
        <Button type="submit" size="sm" disabled={saving} data-testid="submit-add-participant">
          {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
          {saving ? 'Wird angemeldet...' : 'Anmelden'}
        </Button>
      </div>
    </form>
  );
}

function MessageForm({ event, getToken, hasWaitlist, onSent }) {
  const [subject, setSubject] = useState(`${event.title} – Nachricht vom Organisator`);
  const [message, setMessage] = useState('');
  const [includeWaitlist, setIncludeWaitlist] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) { toast.error('Bitte Nachricht eingeben'); return; }
    setSending(true);
    try {
      const token = getToken();
      const { data } = await axios.post(`${API_URL}/api/events/${event.id}/organizer-message`,
        { subject, message, include_waitlist: includeWaitlist },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Nachricht an ${data.sent} Teilnehmer gesendet`);
      if (data.failed > 0) toast.warning(`${data.failed} Zustellungen fehlgeschlagen`);
      onSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    } finally { setSending(false); }
  };

  return (
    <form onSubmit={handleSend} className="px-4 py-3 border-b border-border space-y-3" data-testid="organizer-message-form">
      <div>
        <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Betreff</label>
        <Input value={subject} onChange={e => setSubject(e.target.value)} className="rounded-none" required data-testid="message-subject-input" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Nachricht</label>
        <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} className="rounded-none"
          placeholder="Deine Nachricht an die Teilnehmer..." required data-testid="message-text-input" />
      </div>
      {hasWaitlist && (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={includeWaitlist} onChange={e => setIncludeWaitlist(e.target.checked)} />
          Auch an Warteliste senden
        </label>
      )}
      <div className="flex justify-end">
        <Button type="submit" disabled={sending} data-testid="send-message-btn">
          {sending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
          {sending ? 'Wird gesendet...' : 'Nachricht senden'}
        </Button>
      </div>
    </form>
  );
}
